import minBy from "lodash/minBy";
import uniqBy from "lodash/uniqBy";
import { clamp01, distToRect } from "views/components/visualizations/OnClickPopover/anchors";
import dropdownPopover from "views/components/visualizations/OnClickPopover/dropdownPopover";
import CartesianOnClickPopoverDropdown from "views/components/visualizations/OnClickPopover/CartesianOnClickPopoverDropdown";
import { CANDIDATE_PICK_RADIUS } from "views/components/visualizations/Constants";
const isDefined = (value) => !!value;
const getScatterMarkerElement = (graphDiv, pt) => {
  var _a;
  const {
    fullData: { uid },
    pointIndex
  } = pt;
  const trace = graphDiv.querySelector(`.scatterlayer .trace.trace${uid}`);
  if (!trace) return null;
  return (_a = trace.querySelectorAll(".points .point")[pointIndex]) != null ? _a : null;
};
const projectT = (P, A, B) => {
  const vx = B.x - A.x;
  const vy = B.y - A.y;
  const wx = P.x - A.x;
  const wy = P.y - A.y;
  const len2 = vx * vx + vy * vy || 1;
  return (wx * vx + wy * vy) / len2;
};
const dataToPagePx = (gd, pt, xVal, yVal) => {
  var _a, _b, _c, _d, _e, _f;
  const fl = gd._fullLayout;
  const xa = ((_a = pt.xaxis) == null ? void 0 : _a.l2p) ? pt.xaxis : fl[(_c = (_b = pt.xaxis) == null ? void 0 : _b._name) != null ? _c : "xaxis"];
  const ya = ((_d = pt.yaxis) == null ? void 0 : _d.l2p) ? pt.yaxis : fl[(_f = (_e = pt.yaxis) == null ? void 0 : _e._name) != null ? _f : "yaxis"];
  if (!(xa == null ? void 0 : xa.l2p) || !(xa == null ? void 0 : xa.d2l) || !(ya == null ? void 0 : ya.l2p) || !(ya == null ? void 0 : ya.d2l)) return null;
  const gx = xa.l2p(xa.d2l(xVal));
  const gy = ya.l2p(ya.d2l(yVal));
  if (!Number.isFinite(gx) || !Number.isFinite(gy)) return null;
  const rect = gd.getBoundingClientRect();
  return { x: rect.left + fl._size.l + gx, y: rect.top + fl._size.t + gy };
};
function pickNearestLinesElementForTrace(gd, pt, click) {
  var _a;
  const uid = (_a = pt.fullData) == null ? void 0 : _a.uid;
  if (!uid) return null;
  const trace = gd.querySelector(`.scatterlayer .trace.trace${uid}`);
  if (!trace) return null;
  const linesGroups = Array.from(trace.querySelectorAll(".lines path.js-line"));
  if (!linesGroups.length) return null;
  const candidates = linesGroups.map((line) => {
    const r = line.getBoundingClientRect();
    const { x, y } = click;
    return { el: line, d: distToRect(r, { x, y }) };
  });
  const best = minBy(candidates, "d");
  return best == null ? void 0 : best.el;
}
const getScatterLineElements = (gd, click, pt) => {
  var _a, _b, _c, _d, _e, _f;
  const fd = (_b = pt.data) != null ? _b : (_a = gd._fullData) == null ? void 0 : _a[pt.curveNumber];
  const xs = (_c = fd == null ? void 0 : fd.x) != null ? _c : [];
  const ys = (_d = fd == null ? void 0 : fd.y) != null ? _d : [];
  const i = (_f = (_e = pt.pointIndex) != null ? _e : pt.pointNumber) != null ? _f : 0;
  const segs = [i > 0 ? [i - 1, i] : null, i < xs.length - 1 ? [i, i + 1] : null].filter((seg) => !!seg);
  return segs.map(([i0, i1]) => {
    const A = dataToPagePx(gd, pt, xs[i0], ys[i0]);
    const B = dataToPagePx(gd, pt, xs[i1], ys[i1]);
    if (!A || !B) return null;
    const t = clamp01(projectT(click, A, B));
    const px = A.x + t * (B.x - A.x);
    const py = A.y + t * (B.y - A.y);
    const d = Math.hypot(click.x - px, click.y - py);
    const el = pickNearestLinesElementForTrace(gd, pt, click);
    if (!el) return null;
    const valuePoint = dataToPagePx(gd, pt, xs[i], ys[i]);
    if (!valuePoint) return null;
    const { x: valuePx, y: valuePy } = valuePoint;
    return { el, pt, d, px, py, valuePx, valuePy };
  });
};
const pointIdentity = (pt) => {
  var _a, _b, _c, _d;
  return `${(_b = (_a = pt.fullData) == null ? void 0 : _a.uid) != null ? _b : pt.curveNumber}:${(_d = (_c = pt.pointIndex) != null ? _c : pt.pointNumber) != null ? _d : pt.x}`;
};
const getPointsInRadius = (candidates) => uniqBy(
  candidates.filter(({ d }) => d < CANDIDATE_PICK_RADIUS).sort((a, b) => a.d - b.d).map(({ pt }) => pt),
  pointIdentity
);
const getScatterRelativeCoordinates = (x, y, rect) => ({
  x: clamp01((x - rect.left) / Math.max(rect.width, 1)),
  y: clamp01((y - rect.top) / Math.max(rect.height, 1))
});
const makeScatterAnchor = (e, gd) => {
  const graphDiv = gd;
  const { clientX, clientY } = e.event;
  const click = { x: clientX, y: clientY };
  const markerCandidates = e.points.map((pt2) => {
    const el2 = getScatterMarkerElement(graphDiv, pt2);
    if (!el2) return null;
    const rect2 = el2.getBoundingClientRect();
    return { type: "marker", pt: pt2, el: el2, rect: rect2, d: distToRect(rect2, click) };
  }).filter(isDefined);
  const lineCandidates = e.points.flatMap((pt2) => getScatterLineElements(graphDiv, click, pt2)).filter(isDefined).map((candidate) => ({ ...candidate, type: "line" }));
  const candidates = [...markerCandidates, ...lineCandidates];
  const pointsInRadius = getPointsInRadius(candidates);
  const best = minBy(candidates, "d");
  if (!best) return null;
  if (best.type === "marker") {
    const { el: el2, rect: rect2, pt: pt2 } = best;
    const rel2 = getScatterRelativeCoordinates(clientX, clientY, rect2);
    return { rel: rel2, el: el2, pt: pt2, pointsInRadius };
  }
  const { el, pt, valuePx, valuePy } = best;
  const rect = el.getBoundingClientRect();
  const rel = getScatterRelativeCoordinates(valuePx, valuePy, rect);
  return { rel, el, pt, pointsInRadius };
};
const scatterOnClickPopover = {
  buildAnchor: makeScatterAnchor,
  renderPopover: dropdownPopover(CartesianOnClickPopoverDropdown)
};
export default scatterOnClickPopover;
